import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';

import { Button, IButtonVariants } from 'components/button';
import { CardNews, CardNewsType } from 'components/card';
import { Icon } from 'components/icon/icon';
import UrlToSvg from 'components/url-to-svg/url-to-svg';

import client from 'api/apollo-client';
import { FeaturedNewsFieldsFragment, Maybe, Newsarticle } from 'api/graphql-types';
import {
	getServerPageGetNewsArticles,
	getServerPageGetOpOostNewsArticles,
	getServerPageGetEfroOostNewsArticles,
	getServerPageGetRegioExpresNewsArticles,
	getServerPageGetThemeDetail,
	getServerPageGetProjectDetail
} from 'api/graphql-helper';

import { useAppSelector } from 'redux/store';
import { Typography } from 'components/typography';

import styles from './featured-news.module.scss';

export interface IFeaturedNewsProps {
	module: FeaturedNewsFieldsFragment;
}

const COLLECTION_TYPES = {
	PROJECTS: 'projecten',
	THEMES: 'themas'
};

const getNewsUrl = (): CardNewsType => {
	let { host } = window.location;

	if (host.includes('localhost')) {
		host = 'www.gelderland.nl';
	}

	if (host.includes('op-oost') || host.includes('regioexpres')) {
		return 'nieuwsberichten';
	}
	return 'nieuws';
};

const FeaturedNews = (props: IFeaturedNewsProps): React.JSX.Element => {
	const router = useRouter();
	const intl = useIntl();
	const { publicationState } = useAppSelector((state) => state.settings);

	const [featuredNewsArticles, setFeaturedNewsArticles] = useState<Maybe<Newsarticle[]>>();
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const findRelatedIdBySlug = async (collection: string): Promise<string | undefined> => {
		if (router.asPath.includes(`/${collection}/`)) {
			const variables = { slug: router.asPath.split('/')[2], locale: router.locale, publicationState };

			if (collection === COLLECTION_TYPES.THEMES) {
				const result = await getServerPageGetThemeDetail({ variables }, client);
				const { themeBySlug } = result.props.data;

				return themeBySlug?.data?.documentId;
			} else if (collection === COLLECTION_TYPES.PROJECTS) {
				const result = await getServerPageGetProjectDetail({ variables }, client);
				const { projectBySlug } = result.props.data;

				return projectBySlug?.data?.documentId;
			}
		}
	};

	const fetchNewsArticles = async (): Promise<any> => {
		let host = window.location.host;

		if (host.includes('localhost')) {
			host = 'www.gelderland.nl';
		}

		const variables = {
			locale: router.locale,
			publicationState,
			limit: 3,
			sort: 'firstPublishedAt:DESC',
			start: 0,
			showOnHomePage: router.asPath === '/' || undefined,
			projectId: await findRelatedIdBySlug(COLLECTION_TYPES.PROJECTS),
			themeIds: await findRelatedIdBySlug(COLLECTION_TYPES.THEMES)
		};

		if (host.includes('regioexpres')) {
			const relatedNewsArticlesResult = await getServerPageGetRegioExpresNewsArticles({ variables }, client);

			if (relatedNewsArticlesResult.props.data.regioExpresNewsarticles_connection?.data.length === 0) {
				const unrelatedNewsArticlesResult = await getServerPageGetRegioExpresNewsArticles({ variables }, client);
				setFeaturedNewsArticles(
					unrelatedNewsArticlesResult.props.data.regioExpresNewsarticles_connection?.data.map(
						(item) => item.attributes as Newsarticle
					)
				);
			}

			setFeaturedNewsArticles(
				relatedNewsArticlesResult.props.data.regioExpresNewsarticles_connection?.data.map(
					(item) => item.attributes as Newsarticle
				)
			);
			return;
		}

		if (host.includes('op-oost')) {
			const relatedNewsArticlesResult = await getServerPageGetOpOostNewsArticles({ variables }, client);

			if (relatedNewsArticlesResult.props.data.opOostNewsarticles_connection?.data.length === 0) {
				const unrelatedNewsArticlesResult = await getServerPageGetOpOostNewsArticles({ variables }, client);
				setFeaturedNewsArticles(
					unrelatedNewsArticlesResult.props.data.opOostNewsarticles_connection?.data.map(
						(item) => item.attributes as Newsarticle
					)
				);
			}

			setFeaturedNewsArticles(
				relatedNewsArticlesResult.props.data.opOostNewsarticles_connection?.data.map(
					(item) => item.attributes as Newsarticle
				)
			);
			return;
		}

		if (host.includes('efro-oost')) {
			const relatedNewsArticlesResult = await getServerPageGetEfroOostNewsArticles({ variables }, client);

			if (relatedNewsArticlesResult.props.data.efroOostNewsarticles_connection?.data.length === 0) {
				const unrelatedNewsArticlesResult = await getServerPageGetEfroOostNewsArticles({ variables }, client);
				setFeaturedNewsArticles(
					unrelatedNewsArticlesResult.props.data.efroOostNewsarticles_connection?.data.map(
						(item) => item.attributes as Newsarticle
					)
				);
			}

			setFeaturedNewsArticles(
				relatedNewsArticlesResult.props.data.efroOostNewsarticles_connection?.data.map(
					(item) => item.attributes as Newsarticle
				)
			);
			return;
		}

		const relatedNewsArticlesResult = await getServerPageGetNewsArticles(
			{
				variables
			},
			client
		);

		if (relatedNewsArticlesResult.props.data.newsarticles_connection?.data.length === 0) {
			const unrelatedNewsArticlesResult = await getServerPageGetNewsArticles(
				{
					variables
				},
				client
			);

			const newsArticles = unrelatedNewsArticlesResult.props.data.newsarticles_connection?.data.map(
				(item) => item.attributes as Newsarticle
			);

			setFeaturedNewsArticles(newsArticles || []);
			return;
		}

		const newsArticles = relatedNewsArticlesResult.props.data.newsarticles_connection?.data.map(
			(item) => item.attributes as Newsarticle
		);

		setFeaturedNewsArticles(newsArticles || []);
	};

	const getNewsArticles = async (): Promise<void> => {
		setIsLoading(true);
		try {
			if (props.module.items && props.module.items.length > 0) {
				const newsArticles = props.module.items.map((item) => item.data.attributes as Newsarticle);

				setFeaturedNewsArticles(newsArticles || []);
			} else {
				await fetchNewsArticles();
			}
		} catch (error: any) {
			// eslint-disable-next-line no-console
			console.error('Error in featured news', error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		getNewsArticles();
	}, [props.module.items]);

	useEffect(() => {
		getNewsArticles();
	}, []);

	return (
		<div className={styles.FeaturedNews}>
			<div className={styles.wrapper}>
				<div className={styles.title}>
					{props.module?.featuredNewsTitle?.value && (
						<Typography tag={props.module?.featuredNewsTitle?.tag || 'h2'} variant="h2">
							{props.module?.featuredNewsTitle?.value}
						</Typography>
					)}
				</div>
				<div className={styles.content}>
					{featuredNewsArticles && featuredNewsArticles.length > 0
						? featuredNewsArticles?.map((article: Newsarticle, index: number) => (
								<div key={index}>
									<CardNews
										id={`featured-news-${article?.title}`}
										bodyText={article?.bodyText?.bodyText || ''}
										excerpt={article?.header?.excerpt || ''}
										publishedAt={article?.firstPublishedAt}
										slug={article?.slug || ''}
										title={article?.title || ''}
										cover={{
											url:
												article?.header?.cover?.data?.attributes?.formats?.small?.url ||
												article?.header?.cover?.data?.attributes?.url ||
												'',
											alternativeText: article?.header?.cover?.data?.attributes?.alternativeText
										}}
										type={getNewsUrl()}
										nestedTitle
									/>
								</div>
						  ))
						: !isLoading && <div>{intl.formatMessage({ id: 'modules.featuredNews.noArticles' })}</div>}
				</div>
				{props.module?.cta && (
					<div className={styles.button}>
						<Button
							variant={
								props.module?.cta?.variant === 'tertiary'
									? 'tertiary-dark'
									: (props.module?.cta?.variant as unknown as IButtonVariants)
							}
							title={props.module?.cta?.ctaTitle || props.module?.cta?.href?.text || props.module?.cta?.href?.linkText}
							fullWidth
							href={props.module?.cta?.href?.url || props.module?.cta?.href?.linkUrl || '#'}
							icon={
								props.module?.cta?.iconLeft?.data || props.module?.cta?.iconRight?.data ? (
									<Icon
										customIcon={
											<UrlToSvg
												url={
													(props.module?.cta?.iconLeft?.data?.attributes?.url as string) ||
													(props.module.cta?.iconRight?.data?.attributes?.url as string)
												}
											/>
										}
										size="14px"
									/>
								) : undefined
							}
							iconPosition={
								props.module?.cta?.iconLeft?.data ? 'left' : props.module?.cta?.iconRight?.data ? 'right' : undefined
							}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default FeaturedNews;
